import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { Close, Fullscreen, FullscreenExit } from '@mui/icons-material';
import { createKartverketTopoLayer } from '../lib/mapTiles';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function hasCoordinates(lock) {
  return typeof lock.latitude === 'number' && typeof lock.longitude === 'number';
}

function formatDateTime(value) {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString('no-NO');
}

function getLocationSourceLabel(source) {
  if (source === 'location') {
    return 'Eksakt posisjon';
  }

  if (source === 'locationGuess') {
    return 'Estimert posisjon';
  }

  return source || null;
}

function detailValue(value) {
  return value || 'Ikke oppgitt';
}

function createBaseTileLayer() {
  return createKartverketTopoLayer();
}

function invalidateMapSoon(map) {
  if (!map) {
    return;
  }

  requestAnimationFrame(() => {
    map.invalidateSize();
    setTimeout(() => map.invalidateSize(), 50);
  });
}

function MapLegend({ mappedCount, totalCount, compact = false }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: compact ? 1.5 : 2,
        flexWrap: 'wrap',
        alignItems: 'center',
        ...(compact
          ? {
              bgcolor: 'background.paper',
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              boxShadow: 1
            }
          : {})
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'success.main' }} />
        <Typography variant="body2">Tilgjengelig</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'warning.main' }} />
        <Typography variant="body2">Tilordnet / i bruk</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#7b1fa2' }} />
        <Typography variant="body2">Tvilling</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {mappedCount} av {totalCount} låser har posisjon
      </Typography>
    </Box>
  );
}

export default function LocksMap({ locks, center }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [fullscreen, setFullscreen] = useState(false);

  const mappedLocks = useMemo(() => locks.filter(hasCoordinates), [locks]);

  const exitFullscreen = useCallback(() => {
    setFullscreen(false);
  }, []);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) {
      return undefined;
    }

    const initialCenter = [
      typeof center?.lat === 'number' ? center.lat : 59.91,
      typeof center?.lng === 'number' ? center.lng : 10.75
    ];

    const map = L.map(containerRef.current, {
      scrollWheelZoom: true,
      center: initialCenter,
      zoom: 12
    });

    createBaseTileLayer().addTo(map);

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // center is only used for first paint; later updates go through the markers effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return undefined;
    }

    // Leaflet requires center/zoom before latLngToContainerPoint / getPixelOrigin.
    if (mappedLocks.length === 1) {
      map.setView([mappedLocks[0].latitude, mappedLocks[0].longitude], 15);
    } else if (mappedLocks.length > 1) {
      const bounds = L.latLngBounds(
        mappedLocks.map((lock) => [lock.latitude, lock.longitude])
      );
      map.fitBounds(bounds, { padding: [32, 32], maxZoom: 16 });
    } else {
      map.setView(
        [
          typeof center?.lat === 'number' ? center.lat : 59.91,
          typeof center?.lng === 'number' ? center.lng : 10.75
        ],
        12
      );
    }

    const layer = L.layerGroup().addTo(map);
    const mapSize = map.getSize();

    mappedLocks.forEach((lock) => {
      const inUse = (lock.assignedResidents || []).length > 0;
      const isTwin = Boolean(lock.duplicateLock);
      const color = isTwin ? '#7b1fa2' : inUse ? '#ed6c02' : '#2e7d32';
      const statusLabel = inUse ? 'Tilordnet' : 'Tilgjengelig';
      const assignedCount = (lock.assignedResidents || []).length;
      const sourceLockId = lock.duplicateSourceLock?.id || null;
      const projectName = lock.duplicateSourceLock?.project?.name || null;
      const projectId = lock.duplicateSourceLock?.project?.id || null;
      const projectOrganizationId = lock.duplicateSourceLock?.project?.organizationId || null;
      const projectOrganization =
        lock.organizationName ||
        lock.duplicateSourceLock?.project?.organizationName ||
        null;
      const sourceLockName = lock.duplicateSourceLock?.name || null;
      const locationSource = getLocationSourceLabel(lock.locationSource);
      const unlocUpdatedOn = formatDateTime(lock.unlocUpdatedOn);
      const showUnlocDetails = lock.provider === 'unloc' || Boolean(
        lock.city ||
          sourceLockId ||
          sourceLockName ||
          projectId ||
          projectName ||
          projectOrganizationId ||
          projectOrganization ||
          lock.vendorType ||
          locationSource ||
          unlocUpdatedOn
      );

      const marker = L.circleMarker([lock.latitude, lock.longitude], {
        radius: 10,
        color,
        weight: 2,
        fillColor: color,
        fillOpacity: 0.9
      });

      const detailContent = [
        `<strong>${escapeHtml(lock.name)}</strong>`,
        `ID: ${escapeHtml(lock.lockId)}`,
        `Lokasjon: ${escapeHtml(lock.location)}`,
        `Leverandør: ${escapeHtml(lock.providerLabel || lock.provider || 'Ukjent')}`,
        projectOrganization ? `Organisasjon: ${escapeHtml(projectOrganization)}` : null,
        isTwin ? 'Tvilling: ja' : null,
        lock.note ? `Notat: ${escapeHtml(lock.note)}` : null,
        showUnlocDetails ? `By: ${escapeHtml(detailValue(lock.city))}` : null,
        showUnlocDetails ? `Kildelås ID: ${escapeHtml(detailValue(sourceLockId))}` : null,
        showUnlocDetails ? `Kildelås navn: ${escapeHtml(detailValue(sourceLockName))}` : null,
        showUnlocDetails ? `Prosjekt-ID: ${escapeHtml(detailValue(projectId))}` : null,
        showUnlocDetails ? `Prosjekt: ${escapeHtml(detailValue(projectName))}` : null,
        showUnlocDetails ? `Organisasjon-ID: ${escapeHtml(detailValue(projectOrganizationId))}` : null,
        showUnlocDetails && !projectOrganization
          ? `Organisasjon: ${escapeHtml(detailValue(projectOrganization))}`
          : null,
        showUnlocDetails ? `Vendor type: ${escapeHtml(detailValue(lock.vendorType))}` : null,
        showUnlocDetails ? `Posisjonskilde: ${escapeHtml(detailValue(locationSource))}` : null,
        showUnlocDetails ? `Unloc oppdatert: ${escapeHtml(detailValue(unlocUpdatedOn))}` : null,
        `${statusLabel} · ${assignedCount} innbygger(e)`,
        `Status: ${escapeHtml(lock.status)}`
      ]
        .filter(Boolean)
        .join('<br/>');

      // Prefer opening overlays toward the map interior — never pan the map.
      let tooltipDirection = 'top';
      let popupOffset = L.point(0, -10);
      if (map._loaded) {
        const point = map.latLngToContainerPoint([lock.latitude, lock.longitude]);
        const nearTop = point.y < 180;
        const nearBottom = point.y > mapSize.y - 140;
        const nearLeft = point.x < 180;
        const nearRight = point.x > mapSize.x - 180;
        if (nearTop) {
          tooltipDirection = 'bottom';
          popupOffset = L.point(0, 28);
        } else if (nearBottom) {
          tooltipDirection = 'top';
          popupOffset = L.point(0, -10);
        }
        if (nearLeft) {
          tooltipDirection = 'right';
          popupOffset = L.point(16, nearTop ? 20 : -10);
        } else if (nearRight) {
          tooltipDirection = 'left';
          popupOffset = L.point(-16, nearTop ? 20 : -10);
        }
      }

      marker.bindTooltip(detailContent, {
        sticky: true,
        direction: tooltipDirection,
        offset: tooltipDirection === 'bottom' ? [0, 12] : tooltipDirection === 'right' ? [12, 0] : tooltipDirection === 'left' ? [-12, 0] : [0, -12],
        opacity: 1,
        className: 'locks-map-tooltip'
      });
      marker.bindPopup(detailContent, {
        autoPan: false,
        keepInView: false,
        offset: popupOffset,
        maxWidth: 320,
        maxHeight: 280,
        className: 'locks-map-popup'
      });

      marker.addTo(layer);
    });

    invalidateMapSoon(map);

    return () => {
      layer.remove();
    };
  }, [mappedLocks, center]);

  useEffect(() => {
    invalidateMapSoon(mapRef.current);
  }, [fullscreen]);

  useEffect(() => {
    if (!fullscreen) {
      document.body.style.overflow = '';
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        exitFullscreen();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [fullscreen, exitFullscreen]);

  return (
    <Box>
      {/* Spacer keeps page layout when map is position:fixed */}
      {fullscreen && <Box sx={{ height: { xs: 360, md: 520 }, width: '100%' }} />}

      <Box
        role={fullscreen ? 'dialog' : undefined}
        aria-modal={fullscreen ? true : undefined}
        aria-label={fullscreen ? 'Låsekart fullskjerm' : undefined}
        sx={
          fullscreen
            ? {
                position: 'fixed',
                inset: 0,
                zIndex: (theme) => theme.zIndex.modal + 1,
                bgcolor: 'background.default',
                display: 'flex',
                flexDirection: 'column'
              }
            : undefined
        }
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            mb: fullscreen ? 0 : 1,
            ...(fullscreen
              ? {
                  position: 'absolute',
                  top: 12,
                  left: 12,
                  right: 12,
                  zIndex: 1000,
                  pointerEvents: 'none',
                  '& > *': { pointerEvents: 'auto' }
                }
              : {})
          }}
        >
          {fullscreen ? (
            <MapLegend
              mappedCount={mappedLocks.length}
              totalCount={locks.length}
              compact
            />
          ) : (
            <Box />
          )}
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            {fullscreen ? (
              <Button
                variant="contained"
                size="small"
                startIcon={<FullscreenExit />}
                onClick={exitFullscreen}
                sx={{ boxShadow: 2 }}
              >
                Avslutt
              </Button>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Fullscreen />}
                onClick={() => setFullscreen(true)}
              >
                Fullskjerm
              </Button>
            )}
            {fullscreen && (
              <IconButton
                aria-label="Avslutt fullskjerm"
                onClick={exitFullscreen}
                size="small"
                sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
              >
                <Close />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box
          ref={containerRef}
          sx={
            fullscreen
              ? {
                  position: 'absolute',
                  inset: 0,
                  height: '100%',
                  width: '100%',
                  borderRadius: 0,
                  overflow: 'hidden',
                  border: 'none'
                }
              : {
                  height: { xs: 360, md: 520 },
                  width: '100%',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  '& .leaflet-container': {
                    borderRadius: 2
                  },
                  '& .locks-map-tooltip': {
                    maxWidth: 300,
                    whiteSpace: 'normal'
                  }
                }
          }
        />

        {!fullscreen && (
          <Box sx={{ mt: 1.5 }}>
            <MapLegend mappedCount={mappedLocks.length} totalCount={locks.length} />
          </Box>
        )}
      </Box>
    </Box>
  );
}
